import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookmarkService, isRibbonBookmark } from '../../services/bookmarks';
import { bibleService } from '../../services/bible';
import { t } from '../../i18n/uiText';
import AddBookmarkDialog from './AddBookmarkDialog';
import BfSheet from './bf/BfSheet';

export default function BookmarkModal({
  bookId,
  chapter,
  verse,
  bookName,
  translation = 'synodal',
  onClose,
  onAddBookmark,
  allowMove = false,
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [formattedTitles, setFormattedTitles] = useState({});

  useEffect(() => {
    loadBookmarks();
    loadBooks();
  }, [bookId, chapter, translation]);

  const loadBooks = async () => {
    try {
      const booksData = await bibleService.getBooks(translation);
      setBooks(booksData);
    } catch (error) {
      console.error('Error loading books:', error);
    }
  };

  const sortBookmarksByProximity = (items, currentBookId, currentChapter) => {
    if (!currentBookId || items.length === 0) {
      return items;
    }

    const currentBookIdNum = parseInt(currentBookId);
    const currentChapterNum = currentChapter ? parseInt(currentChapter) : null;

    return [...items].sort((a, b) => {
      const aBookId = parseInt(a.bookId);
      const bBookId = parseInt(b.bookId);
      const aChapter = a.chapter ? parseInt(a.chapter) : null;
      const bChapter = b.chapter ? parseInt(b.chapter) : null;

      const aSameBook = aBookId === currentBookIdNum;
      const bSameBook = bBookId === currentBookIdNum;

      if (aSameBook && !bSameBook) return -1;
      if (!aSameBook && bSameBook) return 1;

      if (aSameBook && bSameBook && currentChapterNum) {
        const aChapterDiff = aChapter ? Math.abs(aChapter - currentChapterNum) : Infinity;
        const bChapterDiff = bChapter ? Math.abs(bChapter - currentChapterNum) : Infinity;

        if (aChapterDiff !== bChapterDiff) {
          return aChapterDiff - bChapterDiff;
        }

        if (aChapter && bChapter) {
          return aChapter - bChapter;
        }
        if (aChapter) return -1;
        if (bChapter) return 1;
        return 0;
      }

      const aBookDiff = Math.abs(aBookId - currentBookIdNum);
      const bBookDiff = Math.abs(bBookId - currentBookIdNum);

      if (aBookDiff !== bBookDiff) {
        return aBookDiff - bBookDiff;
      }

      return aBookId - bBookId;
    });
  };

  const loadBookmarks = async () => {
    try {
      setLoading(true);
      const data = await bookmarkService.getBookmarks();
      const sortedBookmarks = sortBookmarksByProximity((data || []).filter(isRibbonBookmark), bookId, chapter);
      setBookmarks(sortedBookmarks);
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      setBookmarks([]);
      if (error.message && !error.message.includes('No auth token')) {
        alert(`${t('bookmarksLoadError')}: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (books.length > 0 && bookmarks.length > 0) {
      const formatted = {};
      for (const bookmark of bookmarks) {
        const key = `${bookmark.bookId}_${bookmark.chapter || 'null'}_${bookmark.verse || 'null'}`;
        const book = books.find((b) => b.id === bookmark.bookId);
        if (book) {
          if (bookmark.verse) {
            formatted[key] = `${book.name} ${bookmark.chapter}:${bookmark.verse}`;
          } else if (bookmark.chapter) {
            formatted[key] = `${book.name} ${bookmark.chapter}`;
          } else {
            formatted[key] = book.name;
          }
        }
      }
      setFormattedTitles(formatted);
    }
  }, [books, bookmarks]);

  const handleNavigate = async (bookmark, e) => {
    if (e) {
      e.stopPropagation();
    }

    if (allowMove && bookId && chapter) {
      try {
        const verseValue = verse ? parseInt(verse) : null;
        const chapterValue = chapter ? parseInt(chapter) : null;

        await bookmarkService.updateBookmark(
          bookmark.id,
          parseInt(bookId),
          chapterValue,
          verseValue,
          bookmark.note || null
        );

        await loadBookmarks();
        onClose();
      } catch (error) {
        console.error('Error moving bookmark:', error);
        alert(t('bookmarkMoveError'));
      }
    } else {
      const tr = translation || 'kjv';
      let url = `/${tr}/${bookmark.bookId}`;
      if (bookmark.chapter) {
        url += `/${bookmark.chapter}`;
        if (bookmark.verse) {
          url += `/${bookmark.verse}`;
        }
      }
      navigate(url);
      onClose();
    }
  };

  const handleDelete = async (bookmarkId, e) => {
    e.stopPropagation();
    if (window.confirm(t('bookmarkDeleteConfirm'))) {
      try {
        await bookmarkService.deleteBookmark(bookmarkId);
        await loadBookmarks();
      } catch (error) {
        console.error('Error deleting bookmark:', error);
        alert(t('bookmarkDeleteError'));
      }
    }
  };

  const getBookmarkTitle = (bookmark) => {
    const key = `${bookmark.bookId}_${bookmark.chapter || 'null'}_${bookmark.verse || 'null'}`;
    return (
      formattedTitles[key] ||
      `${t('bookWord')} ${bookmark.bookId}${bookmark.chapter ? `, ${t('chapterWord')} ${bookmark.chapter}` : ''}${bookmark.verse ? `:${bookmark.verse}` : ''}`
    );
  };

  const isCurrentBookmark = (bookmark) => {
    return (
      bookmark.bookId === parseInt(bookId) &&
      bookmark.chapter === (chapter ? parseInt(chapter) : null) &&
      bookmark.verse === (verse ? parseInt(verse) : null)
    );
  };

  const isMoveMode = allowMove && bookId && chapter;

  return (
    <>
      <BfSheet
        title={isMoveMode ? t('bookmarkChoose') : t('bookmarks')}
        titleMark
        onClose={onClose}
        ariaLabel={isMoveMode ? t('bookmarkChoose') : t('bookmarks')}
        sheetClassName="bf-sheet--fit-content"
        footer={
          bookId && chapter ? (
            <button
              type="button"
              onClick={() => setShowAddDialog(true)}
              className="bf-bm-add"
            >
              <svg className="bf-bm-add__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" />
              </svg>
              <span>{t('createBookmark')}</span>
            </button>
          ) : null
        }
      >
        {loading ? (
          <div className="bf-bm-loading">
            <div className="bf-bm-loading__spinner" aria-hidden="true" />
            <p>{t('bookmarksLoading')}</p>
          </div>
        ) : bookmarks.length === 0 ? (
          <div className="bf-bm-empty">{t('bookmarksEmpty')}</div>
        ) : (
          <div className="bf-bm-list">
            {bookmarks.map((bookmark) => (
              <div
                key={bookmark.id}
                className={`bf-bm-item ${isCurrentBookmark(bookmark) ? 'bf-bm-item--current' : ''}`}
              >
                <div className="bf-bm-item__body">
                  <p className="bf-bm-item__title">
                    {bookmark.note || t('bookmarkUntitled')}
                  </p>
                  <p className="bf-bm-item__sub">
                    <span className="bf-bm-item__sub-mark" aria-hidden="true" />
                    {getBookmarkTitle(bookmark)}
                  </p>
                </div>
                <div className="bf-bm-item__actions">
                  <button
                    type="button"
                    onClick={(e) => handleNavigate(bookmark, e)}
                    className={`bf-bm-go ${isMoveMode ? 'bf-bm-go--select' : ''}`}
                    title={isMoveMode ? t('bookmarkSelectTitle') : t('bookmarkGoTitle')}
                  >
                    {isMoveMode ? (
                      <>
                        <span aria-hidden="true">✓</span>
                        <span>{t('bookmarkSelect')}</span>
                      </>
                    ) : (
                      <>
                        <span aria-hidden="true">→</span>
                        <span>{t('openPassage')}</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(bookmark.id, e)}
                    className="bf-bm-del"
                    title={t('bookmarkDeleteTitle')}
                    aria-label={t('bookmarkDeleteTitle')}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </BfSheet>

      {showAddDialog && (
        <AddBookmarkDialog
          bookId={bookId}
          chapter={chapter}
          verse={verse}
          bookName={bookName}
          onClose={() => setShowAddDialog(false)}
          onBookmarkAdded={async () => {
            if (onAddBookmark) await onAddBookmark();
            await loadBookmarks();
            setShowAddDialog(false);
          }}
        />
      )}
    </>
  );
}
